import React, { useState, useEffect } from 'react';
import IconProvider from './IconProvider';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import {
  addFavorite,
  removeFavorite,
  isItemFavorited,
  favoritesStore,
} from '@/lib/stores/favoritesStore';

interface FavoriteButtonProps {
  itemId: string;
  itemType: 'library' | 'document' | 'blueprint' | 'dialogs';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost';
  className?: string;
  showLabel?: boolean;
}

export const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  itemId,
  itemType,
  size = 'md',
  variant = 'default',
  className = '',
  showLabel = false,
}) => {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const { selectedGame } = useSimplifiedUserData();
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if item is favorited on mount and when favorites change
  useEffect(() => {
    if (!user?.id || !itemId) return;

    const checkFavorited = () => {
      const favorited = isItemFavorited(user.id, itemId);
      setIsFavorited(favorited);
    };

    checkFavorited();

    // Subscribe to store changes to update in real-time
    const unsubscribe = favoritesStore.subscribe(() => {
      checkFavorited();
    });

    return () => unsubscribe();
  }, [user?.id, itemId]);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    // Prevent event from bubbling to parent (e.g., card click)
    e.stopPropagation();
    e.preventDefault();

    if (!user?.id || !selectedGame?.id) {
      toast({
        title: 'Error',
        description: 'Please sign in to use favorites',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      if (isFavorited) {
        await removeFavorite(user.id, itemId, toast);
        setIsFavorited(false);
      } else {
        await addFavorite(user.id, selectedGame.id, itemId, itemType, toast);
        setIsFavorited(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Size mappings
  const iconSizes = {
    sm: 14,
    md: 18,
    lg: 24,
  };

  const buttonSizes = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  };

  // Variant styles
  const variantStyles = {
    default: isFavorited
      ? 'bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 border-yellow-300 dark:border-yellow-700'
      : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-600',
    ghost: isFavorited
      ? 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
      : 'hover:bg-gray-100 dark:hover:bg-gray-800',
  };

  const iconColor = isFavorited
    ? '#facc15' // yellow-400
    : '#9ca3af'; // gray-400

  return (
    <button
      onClick={handleToggleFavorite}
      disabled={isLoading}
      className={`
        ${buttonSizes[size]}
        ${variantStyles[variant]}
        inline-flex items-center justify-center
        rounded-md border
        transition-all duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2
        ${variant === 'ghost' ? 'border-transparent' : ''}
        ${className}
      `}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      {isLoading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
      ) : (
        <>
          <IconProvider
            iconstring="star"
            size={iconSizes[size]}
            color={iconColor}
            style={isFavorited ? 'solid' : 'regular'}
          />
          {showLabel && (
            <span className="ml-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              {isFavorited ? 'Favorited' : 'Favorite'}
            </span>
          )}
        </>
      )}
    </button>
  );
};

export default FavoriteButton;

